(function() {
  const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const LOADER_HTML = `
    <div class="pswp__video-loader" aria-hidden="true">
      <svg class="pswp__video-loader-spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" focusable="false">
        <circle class="pswp__video-loader-path" fill="none" stroke-width="5" cx="33" cy="33" r="30"></circle>
      </svg>
    </div>
  `;

  const TOUCH_SHIELD_HTML = '<div class="pswp__video-touch-shield" aria-hidden="true"></div>';
  const embedPlayers = new WeakMap();

  const getLightboxLink = (element) => {
    if (!element) return null;
    if (element.tagName === 'A') return element;
    return element.querySelector('a[data-pswp-type], a[data-pswp-src], a');
  };

  const withEmbedParams = (url) => {
    if (!url) return '';

    try {
      const parsed = new URL(url, window.location.origin);
      const host = parsed.hostname;
      const isYouTube = host.includes('youtube') || host.includes('youtu.be');
      const isVimeo = host.includes('vimeo');

      parsed.searchParams.set('autoplay', '1');
      parsed.searchParams.set('loop', '1');
      parsed.searchParams.set('controls', '0');

      if (isYouTube) {
        parsed.searchParams.set('mute', '1');
        parsed.searchParams.set('playsinline', '1');
        parsed.searchParams.set('enablejsapi', '1');
        const videoId = parsed.pathname.split('/').pop();
        if (videoId && !parsed.searchParams.get('playlist')) {
          parsed.searchParams.set('playlist', videoId);
        }
      } else {
        parsed.searchParams.set('muted', '1');
        if (isVimeo) parsed.searchParams.delete('background');
      }

      return parsed.toString();
    } catch (error) {
      return url;
    }
  };

  const buildVideoHtml = (link, type) => {
    if (type === 'video') {
      const src = escapeHtml(
        link.dataset.pswpVideoSrc || link.getAttribute('href') || link.href || ''
      );
      return `
        <div class="pswp__video-wrapper">
          ${LOADER_HTML}
          <video class="pswp__video" playsinline muted autoplay loop preload="auto" src="${src}"></video>
          ${TOUCH_SHIELD_HTML}
        </div>
      `;
    }

    const src = escapeHtml(withEmbedParams(
      link.dataset.pswpVideoUrl || link.getAttribute('href') || link.href || ''
    ));
    return `
      <div class="pswp__video-wrapper pswp__video-wrapper--external">
        ${LOADER_HTML}
        <iframe class="pswp__video-iframe" src="about:blank" data-pswp-src="${src}" title="Video" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>
        ${TOUCH_SHIELD_HTML}
      </div>
    `;
  };

  const getWrapper = (pswp) => {
    const root = pswp?.currSlide?.content?.element || pswp?.currSlide?.container;
    return root?.querySelector?.('.pswp__video-wrapper') || null;
  };

  const getEmbedHost = (url) => {
    try {
      return new URL(url, window.location.origin).hostname;
    } catch (error) {
      return '';
    }
  };

  const ensureEmbedPlayer = (wrapper, iframe) => {
    const existing = embedPlayers.get(wrapper);
    if (existing) return Promise.resolve(existing);

    const host = getEmbedHost(iframe.src || iframe.dataset.pswpSrc || '');

    if (host.includes('vimeo') && typeof loadVimeoApi === 'function') {
      return loadVimeoApi().then(() => {
        const entry = { type: 'vimeo', player: new window.Vimeo.Player(iframe) };
        embedPlayers.set(wrapper, entry);
        return entry;
      });
    }

    if ((host.includes('youtube') || host.includes('youtu.be')) && typeof loadYouTubeApi === 'function') {
      return loadYouTubeApi().then(() => new Promise((resolve, reject) => {
        const player = new window.YT.Player(iframe, {
          events: {
            onReady: () => {
              const entry = { type: 'youtube', player };
              embedPlayers.set(wrapper, entry);
              resolve(entry);
            },
            onError: reject
          }
        });
      }));
    }

    return Promise.resolve(null);
  };

  const pauseEmbed = (wrapper) => {
    const entry = embedPlayers.get(wrapper);
    if (!entry) return;

    if (entry.type === 'vimeo') {
      entry.player.pause().catch(() => {});
      return;
    }

    if (entry.type === 'youtube') {
      entry.player.pauseVideo?.();
    }
  };

  const playEmbed = (wrapper, iframe, onPlaying) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(safetyTimer);
      onPlaying();
    };
    const safetyTimer = window.setTimeout(finish, 8000);

    ensureEmbedPlayer(wrapper, iframe)
      .then((entry) => {
        if (!entry) {
          finish();
          return;
        }

        if (entry.type === 'vimeo') {
          const onPlay = () => {
            entry.player.off('play', onPlay);
            finish();
          };
          entry.player.on('play', onPlay);
          entry.player.play().catch(() => {});
          entry.player.getPaused().then((paused) => {
            if (!paused) finish();
          }).catch(() => {});
          return;
        }

        if (entry.type === 'youtube') {
          const onStateChange = (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              entry.player.removeEventListener('onStateChange', onStateChange);
              finish();
            }
          };
          entry.player.addEventListener('onStateChange', onStateChange);
          entry.player.mute?.();
          entry.player.playVideo?.();
          if (entry.player.getPlayerState?.() === window.YT.PlayerState.PLAYING) {
            finish();
          }
        }
      })
      .catch(finish);
  };

  const pauseLightboxMedia = (pswp, { keepCurrent = false } = {}) => {
    if (!pswp?.element) return;
    const current = keepCurrent ? getWrapper(pswp) : null;

    pswp.element.querySelectorAll('video.pswp__video').forEach((video) => {
      if (current?.contains(video)) return;
      video.pause();
    });

    pswp.element.querySelectorAll('.pswp__video-wrapper--external').forEach((wrapper) => {
      if (current === wrapper) return;
      pauseEmbed(wrapper);
    });
  };

  const playCurrentMedia = (pswp) => {
    const wrapper = getWrapper(pswp);
    if (!wrapper) return;

    const done = () => wrapper.classList.add('is-loaded');

    const video = wrapper.querySelector('video.pswp__video');
    if (video) {
      if (!wrapper.classList.contains('is-loaded')) {
        video.addEventListener('playing', done, { once: true });
        video.addEventListener('error', done, { once: true });
      }
      video.muted = true;
      video.play()?.catch?.(() => {});
      return;
    }

    const iframe = wrapper.querySelector('iframe.pswp__video-iframe');
    const src = iframe?.dataset?.pswpSrc;
    if (!src) return;

    if (iframe.src && iframe.src !== 'about:blank' && embedPlayers.has(wrapper)) {
      playEmbed(wrapper, iframe, done);
      return;
    }

    wrapper.classList.remove('is-loaded');

    const onLoad = () => {
      if (!iframe.src || iframe.src === 'about:blank') return;
      iframe.removeEventListener('load', onLoad);
      playEmbed(wrapper, iframe, done);
    };

    if (iframe.src && iframe.src !== 'about:blank') {
      playEmbed(wrapper, iframe, done);
      return;
    }

    iframe.addEventListener('load', onLoad);
    iframe.src = src;
  };

  const lightbox = () => {
    const leftArrowSVGString = '<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18"><path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square" stroke-width="2"></path></svg>';
    const closeSvgString = '<svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16"><path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1.2"></path></svg>';
    const lightboxInstance = new PhotoSwipeLightbox({
      gallery: '.splide__gallery',
      children: '.product-media-zoom',
      bgOpacity: 1,
      showHideAnimationType: 'zoom',
      loop: true,
      counter: true,
      zoom: false,
      bgClickAction: 'close',
      tapAction: (point, event) => {
        if (event?.target?.closest?.('.pswp__button, button')) {
          return;
        }
        lightboxInstance.pswp?.close();
      },
      arrowPrevSVG: leftArrowSVGString,
      arrowNextSVG: leftArrowSVGString,
      closeSVG: closeSvgString,
      pswpModule: PhotoSwipe
    });

    lightboxInstance.addFilter('itemData', (itemData) => {
      const link = getLightboxLink(itemData.element);
      if (!link) return itemData;

      const type = link.dataset.pswpType;
      if (type !== 'video' && type !== 'external-video') return itemData;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const width = Number(link.dataset.pswpWidth) || Number(itemData.width) || 2000;
      const height = Number(link.dataset.pswpHeight) || Number(itemData.height) || Math.round(width / (16 / 9));

      itemData.width = isMobile ? Math.max(1, window.innerWidth) : width;
      itemData.height = isMobile ? Math.max(1, window.innerHeight) : height;
      itemData.w = itemData.width;
      itemData.h = itemData.height;
      itemData.type = 'html';
      delete itemData.src;
      itemData.html = buildVideoHtml(link, type);

      return itemData;
    });

    lightboxInstance.on('beforeOpen', () => {
      document.body.classList.add('overflow-hidden');
    });
    lightboxInstance.on('openingAnimationEnd', () => playCurrentMedia(lightboxInstance.pswp));
    lightboxInstance.on('change', () => {
      pauseLightboxMedia(lightboxInstance.pswp, { keepCurrent: true });
      playCurrentMedia(lightboxInstance.pswp);
    });
    lightboxInstance.on('close', () => {
      pauseLightboxMedia(lightboxInstance.pswp);
      document.body.classList.remove('overflow-hidden');
    });
    lightboxInstance.init();
  };

  document.addEventListener('DOMContentLoaded', () => {
    lightbox();
    document.addEventListener('shopify:section:load', () => lightbox());
  });
})();
